import React, {useEffect, useState} from 'react';

import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {gt} from 'app/utils/i18n';

import {InfiniteSearchResultsList} from './InfiniteSearchResultsList';
import {useDocumentLoader} from './user-document-loader';

import type {AssignedDocType} from './types';
import type {WizardFormPaneProps} from 'app/general/widgets/WizardAccordion';
import type {TaskAssignableContentTypes} from 'sweet-types';

interface Props extends WizardFormPaneProps<AssignedDocType | null> {
  onPickDocument: (result: AssignedDocType) => unknown;
}

export const PickDocumentForm = React.memo(function PickDocumentForm({
  onPickDocument,
  FooterComponent,
}: Props): JSX.Element {
  // querystring used for search
  const [search, setSearch] = useState('');

  // selected search result HTML_URL
  const [selected, setSelected] = useState<{
    contentType: TaskAssignableContentTypes;
    objectId: number;
  } | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const {currentData: selectedDoc, isFetching: isLoadingDocDetails} =
    useDocumentLoader(selected);

  useEffect(() => {
    setSelected(null);
  }, [search]);

  useEffect(() => {
    // go to next page after fetching document details
    if (selectedDoc != null) {
      if (selectedDoc.can_assign) {
        onPickDocument(selectedDoc);
      }
    }
  }, [selectedDoc]);

  return (
    <>
      <form>
        <div className="form-group mb-0">
          <label htmlFor="select-doc-field">
            {gt('Choose from existing Processes or Procedures')}
          </label>
          <SearchInputField
            onChange={setSearch}
            initialValue={search}
            placeholder={gt('Search...')}
            autoFocus={true}
            id="select-doc-field"
            isSearching={isFetching}
          />
        </div>
        <div className="border-bottom">
          <InfiniteSearchResultsList
            search={search}
            selected={selected}
            onSelect={setSelected}
            onIsFetchingChange={setIsFetching}
            isLoadingDocDetails={isLoadingDocDetails}
          />

          {selectedDoc?.can_assign === false ? (
            <div className="alert alert-warning my-4 small" role="alert">
              {gt('The selected document can only be assigned to yourself')}
            </div>
          ) : null}
        </div>
      </form>
      <FooterComponent
        isCompleted={selectedDoc != null && !isLoadingDocDetails}
        result={selectedDoc ?? null}
      />
    </>
  );
});
